import type { AdvisorNotification } from "@repo/shared/types";
import { useCallback } from "react";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./use-notification-text.i18n";

export type NotificationText = {
  /** What happened, in the user's language. */
  title: string;
  /** What it happened to: "flow · client", or the ticket title. */
  detail: string;
};

/**
 * QL-728 — the words for a notification, rendered locally from its `event` and
 * the display fields captured in `data` (the API sends no text). Shared by the
 * bell's list and its toast.
 */
export function useNotificationText(): (notification: AdvisorNotification) => NotificationText {
  const t = useT(T);
  // Stable per language, so callers can list it as an effect dependency.
  return useCallback(
    ({ event, data }: AdvisorNotification): NotificationText => {
      if (event === "support.reply") {
        return { title: t.supportReply, detail: data.ticketTitle || t.unnamedTicket };
      }
      const title: string | undefined = {
        "flow.signature_requested": t.signatureRequested,
        "flow.signed": t.signed,
        "flow.signature_rejected": t.signatureRejected,
        "flow.signature_expired": t.signatureExpired,
        "flow.submitted_by_client": t.submittedByClient,
        "flow.completed": t.completed,
        "flow.compliance_decided":
          data.decision === "rejected" ? t.complianceRejected : t.complianceApproved,
      }[event];
      // An event newer than this build (the API deploys first; tabs stay open for days).
      if (title === undefined) {
        const detail = [data.flowName, data.clientName, data.ticketTitle]
          .filter(Boolean)
          .join(" · ");
        return { title: t.unknownEvent, detail };
      }
      const detail = [data.flowName || t.unnamedFlow, data.clientName].filter(Boolean).join(" · ");
      return { title, detail };
    },
    [t],
  );
}
